import { FC } from 'react'
import { Typography, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import { useThemeColors } from 'hooks/useThemeColors'

/* Utils */
import { dateFormat } from 'lib/utils'

/* Components */
import { FilterChipWrapper, FilterButton, popperPropsSx } from './filterChip.styles'
import { DateFilterValue } from '../Devices'
import { ChipTooltip } from 'ui/components/tooltip/ChipTooltip'

interface Props {
  filterName: string
  filterValue: string[] | DateFilterValue | string
  displayValue?: string // Pre-formatted display value (truncated)
  isResetButton?: boolean
  handleResetFilter: (colName: string) => void
}

const FilterChip: FC<Props> = ({
  filterName = '',
  filterValue = [''],
  displayValue,
  isResetButton,
  handleResetFilter
}) => {
  const isDateFilter = (value: string[] | DateFilterValue | string): value is DateFilterValue => {
    return typeof value === 'object' && !Array.isArray(value) && 'type' in value
  }

  const isStringValue = (value: string[] | DateFilterValue | string): value is string => {
    return typeof value === 'string'
  }

  // Generate tooltip content from original filterValue
  const getTooltipContent = (): string => {
    if (isStringValue(filterValue)) {
      return filterValue
    }
    if (isDateFilter(filterValue)) {
      if (filterValue.dateTo) {
        return `${dayjs(filterValue.dateFrom).format(dateFormat.historicalDay)} - ${dayjs(filterValue.dateTo).format(
          dateFormat.historicalDay
        )}`
      }
      return filterValue.type === 'before'
        ? `Before: ${dayjs(filterValue.dateFrom).format(dateFormat.historicalDay)}`
        : `After: ${dayjs(filterValue.dateFrom).format(dateFormat.historicalDay)}`
    }
    // Array of strings - join with newlines for better readability
    return (filterValue as string[]).join('\n')
  }

  // Get display text - use displayValue if provided, otherwise format filterValue
  const getDisplayText = (): string => {
    if (displayValue) {
      return displayValue
    }
    if (isStringValue(filterValue)) {
      return filterValue
    }
    if (isDateFilter(filterValue)) {
      if (filterValue.dateTo) {
        return `${dayjs(filterValue.dateFrom).format(dateFormat.historicalDay)} - ${dayjs(filterValue.dateTo).format(
          dateFormat.historicalDay
        )}`
      }
      return filterValue.type === 'before'
        ? `Before: ${dayjs(filterValue.dateFrom).format(dateFormat.historicalDay)}`
        : `After: ${dayjs(filterValue.dateFrom).format(dateFormat.historicalDay)}`
    }
    // Array fallback
    const arr = filterValue as string[]
    if (arr.length === 1) {
      return arr[0]
    }
    return `${arr[0]} (+${arr.length - 1})`
  }

  const tooltipContent = getTooltipContent()
  const displayText = getDisplayText()

  return (
    <FilterChipWrapper className={`filter-chip-wrapper ${isResetButton ? 'reset-wrapper' : ''}`}>
      {isResetButton ? (
        <FilterButton className="filter-chip-reset-button" onClick={() => handleResetFilter('all')}>
          Reset all
        </FilterButton>
      ) : (
        <FilterButton className="filter-chip-button">
          <>
            {filterName.length > 0 && <Typography className="filter-chip-filter-name">{filterName}:</Typography>}
            <ChipTooltip
              placement="bottom-start"
              PopperProps={popperPropsSx}
              title={
                <Typography className="tooltip-text" style={{ whiteSpace: 'pre-line' }}>
                  {tooltipContent}
                </Typography>
              }
            >
              <Typography className="text">{displayText}</Typography>
            </ChipTooltip>
            <IconButton onClick={() => handleResetFilter(filterName)}>
              <CloseIcon />
            </IconButton>
          </>
        </FilterButton>
      )}
    </FilterChipWrapper>
  )
}

export default FilterChip

const CloseIcon = () => {
  const getColor = useThemeColors()

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M4.5 13.5L13.5 4.5M4.5 4.5L13.5 13.5"
        stroke={getColor('menu-item-text')}
        strokeWidth="1.12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
